import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './AboutPage.css';

const AboutPage: React.FC = () => {
  const { language } = useLanguage();

  useEffect(() => {
    // Handle scrolling to hash on page load
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure the page is fully rendered
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-content">
          <h1 className="about-hero-title">
            {language === 'nl'
              ? 'Meijer & Knijnenberg: Jouw Vertrouwde Partner in Finance Recruitment'
              : 'Meijer & Knijnenberg: Your Trusted Partner in Finance Recruitment'
            }
          </h1>
          <p className="about-hero-text">
            {language === 'nl'
              ? 'Bij Meijer & Knijnenberg combineren we meer dan 8 jaar expertise met een persoonlijke en betrokken aanpak binnen finance recruitment. Wij geloven in hoogwaardige en betrouwbare dienstverlening, waarbij jouw wensen en behoeften altijd centraal staan.'
              : 'At Meijer & Knijnenberg, we combine over 8 years of expertise with a personal and committed approach in finance recruitment. We believe in high-quality and reliable service, where your wishes and needs always come first.'
            }
          </p>
          <p className="about-hero-text">
            {language === 'nl'
              ? 'Wij luisteren, denken met je mee en zorgen voor maatwerkoplossingen die écht passen. Bovendien zijn we altijd bereikbaar – want wij begrijpen hoe belangrijk het is om op elk moment op je recruitmentpartner te kunnen rekenen.'
              : 'We listen, think along with you, and provide customized solutions that truly fit. Moreover, we are always accessible – because we understand how important it is to be able to rely on your recruitment partner at any time.'
            }
          </p>
          <p className="about-hero-text">
            {language === 'nl'
              ? 'Onze werkwijze is transparant en risicoloos dankzij ons 100% No Cure, No Pay-principe: geen resultaat, geen kosten. Zo bieden we niet alleen kwaliteit, maar ook zekerheid.'
              : 'Our approach is transparent and risk-free thanks to our 100% No Cure, No Pay principle: no result, no costs. This way, we offer not only quality but also certainty.'
            }
          </p>
          <div className="about-hero-cta">
            <p className="about-hero-tagline">
              {language === 'nl'
                ? 'Ontdek hoe wij het verschil maken in finance recruitment. Meijer & Knijnenberg – persoonlijk, betrouwbaar en altijd bereikbaar.'
                : 'Discover how we make the difference in finance recruitment. Meijer & Knijnenberg – personal, reliable, and always accessible.'
              }
            </p>
          </div>
        </div>
      </section>

      {/* Values Introduction */}
      <section className="values-intro">
        <div className="values-intro-container">
          <h2 className="values-intro-title">
            {language === 'nl'
              ? 'Bij Meijer & Knijnenberg staan toewijding, integriteit en oog voor detail centraal in alles wat we doen.'
              : 'At Meijer & Knijnenberg, dedication, integrity, and attention to detail are central to everything we do.'
            }
          </h2>
          <p className="values-intro-text">
            {language === 'nl'
              ? 'Onze kernwaarden zijn betrouwbaarheid, vakkennis en heldere communicatie. Bij Meijer & Knijnenberg combineren we jarenlange ervaring met een persoonlijke benadering, zodat elke cliënt zich gehoord en ondersteund voelt. Deze waarden vormen de basis van onze werkwijze en zorgen ervoor dat u kunt rekenen op een betrokken en deskundige partner.'
              : 'Our core values are reliability, expertise, and clear communication. At Meijer & Knijnenberg, we combine years of experience with a personal approach, so that every client feels heard and supported. These values form the foundation of our working method and ensure that you can count on a committed and expert partner.'
            }
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section className="core-values">
        <div className="core-values-container">
          <div className="value-card">
            <div className="value-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            <h3 className="value-title">
              {language === 'nl' ? 'Betrouwbaarheid' : 'Reliability'}
            </h3>
            <p className="value-description">
              {language === 'nl'
                ? 'Betrouwbaarheid betekent voor ons dat afspraken worden nagekomen, verwachtingen duidelijk zijn en cliënten kunnen rekenen op een constante kwaliteit. Bij Meijer & Knijnenberg streven we naar langdurige samenwerkingen, gebouwd op wederzijds vertrouwen. We communiceren helder, handelen transparant en zorgen dat u altijd weet waar u aan toe bent. Dat maakt ons een partner waarop u kunt bouwen.'
                : 'For us, reliability means that agreements are honored, expectations are clear, and clients can count on consistent quality. At Meijer & Knijnenberg, we strive for long-term collaborations built on mutual trust. We communicate clearly, act transparently, and ensure you always know where you stand. That makes us a partner you can build on.'
              }
            </p>
          </div>

          <div className="value-card">
            <div className="value-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
              </svg>
            </div>
            <h3 className="value-title">
              {language === 'nl' ? 'Vakkennis' : 'Expertise'}
            </h3>
            <p className="value-description">
              {language === 'nl'
                ? 'Vakkennis is de basis van ons werk. Onze expertise is opgebouwd uit jarenlange ervaring, voortdurende bijscholing en een scherp inzicht in actuele ontwikkelingen binnen ons vakgebied. We denken niet alleen met u mee, maar vaak ook een stap vooruit. Zo bieden we oplossingen die niet alleen degelijk, maar ook doordacht zijn.'
                : 'Expertise is the foundation of our work. Our knowledge is built on years of experience, continuous training, and keen insight into current developments in our field. We not only think along with you but often think one step ahead. This way, we offer solutions that are not only solid but also well-considered.'
              }
            </p>
          </div>

          <div className="value-card">
            <div className="value-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
            </div>
            <h3 className="value-title">
              {language === 'nl' ? 'Heldere communicatie' : 'Clear Communication'}
            </h3>
            <p className="value-description">
              {language === 'nl'
                ? 'Heldere communicatie zorgt voor vertrouwen en efficiëntie. We zeggen wat we doen, en we doen wat we zeggen. Bij Meijer & Knijnenberg geloven we in duidelijke taal, korte lijnen en open overleg. Zo voorkomen we misverstanden en zorgen we ervoor dat u altijd betrokken bent bij elke stap in het proces.'
                : 'Clear communication ensures trust and efficiency. We say what we do, and we do what we say. At Meijer & Knijnenberg, we believe in clear language, short lines, and open consultation. This way, we prevent misunderstandings and ensure that you are always involved in every step of the process.'
              }
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="team-section">
        <div className="team-container">
          <h2 className="team-title">
            {language === 'nl' ? 'Ontmoet ons team' : 'Meet Our Team'}
          </h2>
          <div className="team-grid">
            <div className="team-member">
              <div className="team-member-image">
                <img
                  src="/assets/team/thiemo.jpeg"
                  alt="Thiemo Meijer"
                />
              </div>
              <div className="team-member-info">
                <h3 className="team-member-name">Thiemo Meijer</h3>
                <p className="team-member-role">Co-Founder</p>
                <div className="team-member-contacts">
                  <a href="tel:+31640486192" className="team-member-contact">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                    </svg>
                    +31 6 40 48 61 92
                  </a>
                  <a href="mailto:thiemo@meijerknijnenberg.com" className="team-member-contact">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    thiemo@meijerknijnenberg.com
                  </a>
                </div>
              </div>
            </div>

            <div className="team-member">
              <div className="team-member-image">
                <img
                  src="/assets/team/mats.jpeg"
                  alt="Mats Knijnenberg"
                />
              </div>
              <div className="team-member-info">
                <h3 className="team-member-name">Mats Knijnenberg</h3>
                <p className="team-member-role">Co-Founder</p>
                <div className="team-member-contacts">
                  <a href="tel:+31611727668" className="team-member-contact">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                    </svg>
                    +31 6 11 72 76 68
                  </a>
                  <a href="mailto:mats@meijerknijnenberg.com" className="team-member-contact">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    mats@meijerknijnenberg.com
                  </a>
                </div>
              </div>
            </div>

            <div className="team-member">
              <div className="team-member-image">
                <img
                  src="/assets/team/nils.jpeg"
                  alt="Nils Zwartepoorte"
                />
              </div>
              <div className="team-member-info">
                <h3 className="team-member-name">Nils Zwartepoorte</h3>
                <p className="team-member-role">Talent Sourcer</p>
                <div className="team-member-contacts">
                  <a href="tel:+31203086271" className="team-member-contact">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                    </svg>
                    +31 20 308 6271
                  </a>
                  <a href="mailto:nils@meijerknijnenberg.com" className="team-member-contact">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    nils@meijerknijnenberg.com
                  </a>
                </div>
              </div>
            </div>

            <div className="team-member">
              <div className="team-member-image">
                <img
                  src="/assets/team/ruben.jpeg"
                  alt="Ruben Postma"
                />
              </div>
              <div className="team-member-info">
                <h3 className="team-member-name">Ruben Postma</h3>
                <p className="team-member-role">Talent Sourcer</p>
                <div className="team-member-contacts">
                  <a href="tel:+31203086271" className="team-member-contact">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                    </svg>
                    +31 20 308 6271
                  </a>
                  <a href="mailto:ruben@meijerknijnenberg.com" className="team-member-contact">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    ruben@meijerknijnenberg.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
